import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiPrinter } from 'react-icons/fi';
import { fieldLabel, roleLabel } from './activityMeta';
import { ChangeList } from './SuperAdminActivity';

const fmtDateTime = (d) => (d
  ? new Date(d).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
  : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const money = (n) => `SAR ${(n || 0).toFixed(2)}`;
const whoLabel = (u) => (u ? `${u.name} (${roleLabel(u.role)})` : '—');

const EVENT_STYLE = {
  customer: { color: '#64748b', label: 'Profile' },
  order: { color: '#3b82f6', label: 'Order' },
  bill: { color: '#8b5cf6', label: 'Bill' },
  payment: { color: '#10b981', label: 'Payment' },
  correction: { color: '#f59e0b', label: 'Advance changed' },
  ready: { color: '#0ea5e9', label: 'Ready' },
  delivered: { color: '#059669', label: 'Delivered' },
  modified: { color: '#f59e0b', label: 'Modified' },
  edit: { color: '#f59e0b', label: 'Edited' },
  deleted: { color: '#ef4444', label: 'Deleted' },
};

const SHIRT = ['length', 'chest', 'middle', 'shoulder', 'arm', 'kback', 'neck', 'head'];
const PANT = ['pantLength', 'waist', 'hip', 'thigh', 'knee', 'bottom', 'round'];
const STYLE = ['napel', 'collarPoint', 'collar', 'chestStyle', 'frontStyle', 'namePlate', 'frontPocket', 'sleevePocket', 'rutba', 'flop', 'cuffStyle', 'nameEmbroidery', 'buttonSize', 'pleats', 'pantWaistStyle', 'pantBottomStyle', 'pantPocketStyle'];
const OTHER_FIRST = ['fullNameLabel', 'orderType', 'suitQuantity', 'type', 'clothLabel', 'clothLabelOther', 'extraInformation'];
const HIDDEN = ['price', 'discount', 'advance', 'remaining', 'isPending', 'isDelivered', 'isReady', 'readyAt', 'pickupDate', 'deliveryDate', 'paymentType', 'paymentStatus', 'shirtPrice', 'pantPrice', 'styleImage', 'extraItems'];

const fmtVal = (v) => (Array.isArray(v) ? v.join(' + ') : typeof v === 'object' ? JSON.stringify(v) : String(v));
const Chips = ({ keys, d }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {keys.filter(k => d[k] !== undefined).map(k => (
      <div key={k} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', minWidth: 96 }}>
        <div style={{ fontSize: 11, color: '#64748b' }}>{fieldLabel(k)}</div>
        <div style={{ fontWeight: 700 }}>{fmtVal(d[k])}</div>
      </div>
    ))}
  </div>
);
const Group = ({ title, keys, d }) => (keys.some(k => d[k] !== undefined)
  ? <div style={{ marginTop: 12 }}><div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .4 }}>{title}</div><Chips keys={keys} d={d} /></div>
  : null);

const Stat = ({ label, value, color }) => (
  <div className="card" style={{ flex: '1 1 150px', minWidth: 150 }}>
    <div className="card-body" style={{ padding: 14 }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || '#1e293b', marginTop: 4 }}>{value}</div>
    </div>
  </div>
);

const Section = ({ title, count, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-header"><strong>{title}</strong>{count !== undefined && <span style={{ color: '#64748b', fontSize: 13 }}>{count}</span>}</div>
    {children}
  </div>
);

// Read-only, complete customer report for the Super Admin: profile, money
// totals, every order, every bill (who created it and when), every payment
// (who took it and when) and one chronological timeline.
export default function SuperAdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminApi.get(`/customers/${id}`).then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Customer not found'); navigate('/superadmin/customers'); });
  }, [id, navigate]);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!data) return null;
  const { customer, summary, orders, bills, payments, timeline } = data;

  return (
    <div>
      <div className="no-print" style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/superadmin/customers')}><FiArrowLeft size={14} /> Back</button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}><FiPrinter size={14} /> Print report</button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{customer.name}</div>
            <div style={{ color: '#64748b', marginTop: 2 }}>📞 {customer.phone}{customer.city ? ` · ${customer.city}` : ''}{customer.email ? ` · ${customer.email}` : ''}</div>
            <div style={{ marginTop: 8 }}><span className="badge badge-gray">{customer.shop?.name || 'Unknown shop'}</span></div>
          </div>
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
            <div>Customer added: <strong>{fmtDateTime(customer.createdAt)}</strong></div>
            <div>Added by: <strong>{whoLabel(customer.createdBy)}</strong></div>
            <div>First order: <strong>{fmtDateTime(summary.firstOrderAt)}</strong></div>
            <div>Last activity: <strong>{fmtDateTime(summary.lastActivityAt)}</strong></div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Orders (clothes made)" value={summary.orders} />
        <Stat label="Bills created" value={summary.bills} />
        <Stat label="Total billed" value={money(summary.totalBilled)} />
        <Stat label="Total paid" value={money(summary.totalPaid)} color="#10b981" />
        <Stat label="Remaining (credit)" value={money(summary.totalRemaining)} color={summary.totalRemaining > 0 ? '#ef4444' : '#10b981'} />
        <Stat label="Discount given" value={money(summary.totalDiscount)} color="#d97706" />
        <Stat label="Delivered / Pending" value={`${summary.delivered} / ${summary.pendingDelivery}`} />
        <Stat label="Payments received" value={summary.paymentCount} />
        <Stat label="Times edited" value={summary.edits} color={summary.edits ? '#f59e0b' : undefined} />
      </div>

      <Section title="Orders" count={`${orders.length}`}>
        <div className="table-container"><table>
          <thead><tr><th>Order #</th><th>Dress</th><th>Type</th><th>Created</th><th>Delivery</th><th>Price</th><th>Discount</th><th>Paid</th><th>Remaining</th><th>Status</th><th>Billed by</th></tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', color: '#94a3b8' }}>No orders</td></tr>}
            {orders.map(o => (
              <tr key={o._id}>
                <td><strong>{o.orderNumber || '—'}</strong>{o.billNumber && <div style={{ fontSize: 11, color: '#94a3b8' }}>Bill #{o.billNumber}</div>}</td>
                <td>{o.dressName || '—'}<div style={{ fontSize: 11, color: '#94a3b8' }}>{o.clothLabel || ''}</div></td>
                <td>{(o.types || []).join(' + ') || '—'}<div style={{ fontSize: 11, color: '#94a3b8' }}>{o.orderType || ''}{o.quantity > 1 ? ` · x${o.quantity}` : ''}</div></td>
                <td>{fmtDateTime(o.createdAt)}</td>
                <td>{fmtDate(o.deliveryDate)}</td>
                <td>{money(o.price)}</td>
                <td>{o.discount ? money(o.discount) : '—'}</td>
                <td style={{ color: '#10b981', fontWeight: 600 }}>{money(o.advance)}</td>
                <td style={{ color: o.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>{money(o.remaining)}</td>
                <td>
                  {o.isDelivered ? <span className="badge badge-success">Delivered</span>
                    : o.isReady ? <span className="badge badge-info">Ready</span>
                    : <span className="badge badge-warning">In progress</span>}
                  {o.isDelivered && o.deliveredAt && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDateTime(o.deliveredAt)}</div>}
                </td>
                <td>{whoLabel(o.billedBy)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Section>

      <Section title="Measurements & style details (per order)" count={`${orders.length}`}>
        <div className="card-body">
          {orders.length === 0 && <div style={{ color: '#94a3b8' }}>No orders</div>}
          {orders.map((o, i) => {
            const d = o.details || {};
            const known = [...SHIRT, ...PANT, ...STYLE, ...OTHER_FIRST, ...HIDDEN, 'suitQuantity'];
            const rest = Object.keys(d).filter(k => !known.includes(k));
            return (
              <div key={o._id} style={{ padding: '12px 0', borderBottom: i < orders.length - 1 ? '2px solid #e2e8f0' : 'none' }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{o.orderNumber || `Order ${i + 1}`} — {o.dressName || 'Order'} <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>· created {fmtDateTime(o.createdAt)}</span></div>
                <Group title="Order" keys={OTHER_FIRST} d={d} />
                <Group title="Shirt measurements" keys={SHIRT} d={d} />
                <Group title="Trouser measurements" keys={PANT} d={d} />
                <Group title="Style options" keys={STYLE} d={d} />
                <Group title="Other details" keys={rest} d={d} />
                {Array.isArray(d.extraItems) && d.extraItems.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>Extra items</div>
                    {d.extraItems.map((x, j) => <div key={j} style={{ fontSize: 13 }}>{x.name} × {x.quantity || 1} — {money(x.sellPrice)}</div>)}
                  </div>
                )}
                {d.styleImage && <div style={{ marginTop: 12 }}><a href={d.styleImage} target="_blank" rel="noreferrer">View style image →</a></div>}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Edit history — who changed what" count={`${(data.edits || []).length} edits`}>
        <div className="card-body">
          {(data.edits || []).length === 0 && <div style={{ color: '#94a3b8' }}>No edits recorded since activity tracking started.</div>}
          {(data.edits || []).map((e, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 14 }}><strong style={{ color: e.action === 'bill_deleted' ? '#ef4444' : '#f59e0b' }}>{e.action === 'bill_deleted' ? 'Deleted' : 'Edited'}</strong> — {e.summary}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(e.at)} · by {whoLabel(e.by)}</div>
              {e.changes && e.changes.length > 0 && <ChangeList changes={e.changes} />}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Bills" count={`${bills.length}`}>
        <div className="table-container"><table>
          <thead><tr><th>Bill #</th><th>Order #</th><th>Created at</th><th>Created by</th><th>Total</th><th>Discount</th><th>Paid</th><th>Remaining</th><th>Status</th><th>Last modified</th></tr></thead>
          <tbody>
            {bills.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: '#94a3b8' }}>No bills</td></tr>}
            {bills.map(b => (
              <tr key={b._id}>
                <td><span style={{ fontWeight: 700, color: '#3b82f6', cursor: 'pointer' }} onClick={() => navigate(`/superadmin/sales/${b._id}`)}>#{b.billNumber}</span></td>
                <td>{b.orderNumber || '—'}</td>
                <td>{fmtDateTime(b.createdAt)}</td>
                <td>{whoLabel(b.createdBy)}</td>
                <td><strong>{money(b.total)}</strong></td>
                <td>{b.discount ? money(b.discount) : '—'}</td>
                <td style={{ color: '#10b981', fontWeight: 600 }}>{money(b.advancePaid)}</td>
                <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>{money(b.remaining)}</td>
                <td><span className={`badge badge-${b.status === 'paid' ? 'success' : b.status === 'partial' ? 'info' : 'warning'}`}>{b.status}</span></td>
                <td>{new Date(b.updatedAt) - new Date(b.createdAt) > 60000 ? fmtDateTime(b.updatedAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Section>

      <Section title="Payments" count={`${payments.length}`}>
        <div className="table-container"><table>
          <thead><tr><th>Date & time</th><th>Amount</th><th>Method</th><th>Bill #</th><th>Received by</th><th>Note</th></tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No payments recorded</td></tr>}
            {payments.map((p, i) => (
              <tr key={i}>
                <td>{fmtDateTime(p.at)}</td>
                <td style={{ fontWeight: 700, color: p.amount < 0 ? '#f59e0b' : '#10b981' }}>{p.amount < 0 ? '-' : ''}{money(Math.abs(p.amount))}</td>
                <td>{p.method || '—'}</td>
                <td>#{p.billNumber}</td>
                <td>{whoLabel(p.by)}</td>
                <td>{p.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Section>

      <Section title="Full timeline (newest first)" count={`${timeline.length} events`}>
        <div className="card-body">
          {timeline.map((e, i) => {
            const st = EVENT_STYLE[e.type] || EVENT_STYLE.customer;
            return (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: i < timeline.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: st.color, marginTop: 6, flex: '0 0 10px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}><span style={{ color: st.color, fontWeight: 700 }}>{st.label}</span> — {e.title}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(e.at)}{e.by ? ` · by ${whoLabel(e.by)}` : ''}</div>
                  {e.changes && e.changes.length > 0 && <ChangeList changes={e.changes} />}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <div className="no-print" style={{ fontSize: 12, color: '#94a3b8', marginBottom: 24 }}>
        Note: orders, bills and payments show their full history. Who-did-what and before/after values of edits are recorded from the moment activity tracking went live; earlier edits only show as "last modified" with their time.
      </div>
    </div>
  );
}
